import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "../../config/api";

const initialState = {
 categories: [],
 products: [],
 productPagination: {
  page: 1,
  limit: 100,
  total: 0,
  totalPages: 1,
 },
 enquiries: [],
 sliders: [],
 aboutSliders: [],
 homeJourneyImages: [],
 homeCommunityImages: [],
 customerFeedback: [],
 scootyFeatureConfig: null,
 systemCertifications: [],
 productCertifications: [],
 status: { type: "idle", text: "Sign in to continue." },
 loading: false,
};

function getToken(getState) {
 return getState().adminAuth?.token || localStorage.getItem("mozoAdminToken") || "";
}

export const loadAdminData = createAsyncThunk(
 "admin/loadAdminData",
 async (_, { getState }) => {
  const token = getToken(getState);
  const [
   categoryResult,
   productResult,
   enquiryResult,
   sliderResult,
   aboutSliderResult,
   journeyImageResult,
   communityImageResult,
   feedbackResult,
   scootyFeatureResult,
   systemCertificationResult,
   productCertificationResult,
  ] =
   await Promise.allSettled([
    apiRequest("/categories", {}, token),
    apiRequest("/products?limit=100&sort=featured", {}, token),
    apiRequest("/enquiries", {}, token),
    apiRequest("/admin/sliders", {}, token),
    apiRequest("/admin/about-sliders", {}, token),
    apiRequest("/admin/home-images/journey", {}, token),
    apiRequest("/admin/home-images/community", {}, token),
    apiRequest("/admin/customer-feedback", {}, token),
    apiRequest("/admin/scooty-feature-config", {}, token),
    apiRequest("/admin/certifications/system", {}, token),
    apiRequest("/admin/certifications/product", {}, token),
   ]);

  const failures = [
   categoryResult,
   productResult,
   enquiryResult,
   sliderResult,
   aboutSliderResult,
   journeyImageResult,
   communityImageResult,
   feedbackResult,
   scootyFeatureResult,
   systemCertificationResult,
   productCertificationResult,
  ]
   .filter((result) => result.status === "rejected")
   .map((result) => result.reason.message);

  return {
   categories: categoryResult.status === "fulfilled" ? categoryResult.value.categories || [] : [],
   products: productResult.status === "fulfilled" ? productResult.value.products || [] : [],
   productPagination:
    productResult.status === "fulfilled" ? productResult.value.pagination : initialState.productPagination,
   enquiries: enquiryResult.status === "fulfilled" ? enquiryResult.value.enquiries || [] : [],
   sliders: sliderResult.status === "fulfilled" ? sliderResult.value.sliders || [] : [],
   aboutSliders: aboutSliderResult.status === "fulfilled" ? aboutSliderResult.value.sliders || [] : [],
   homeJourneyImages: journeyImageResult.status === "fulfilled" ? journeyImageResult.value.images || [] : [],
   homeCommunityImages: communityImageResult.status === "fulfilled" ? communityImageResult.value.images || [] : [],
   customerFeedback: feedbackResult.status === "fulfilled" ? feedbackResult.value.feedback || [] : [],
   scootyFeatureConfig: scootyFeatureResult.status === "fulfilled" ? scootyFeatureResult.value.config || null : null,
   systemCertifications: systemCertificationResult.status === "fulfilled" ? systemCertificationResult.value.certifications || [] : [],
   productCertifications: productCertificationResult.status === "fulfilled" ? productCertificationResult.value.certifications || [] : [],
   failures,
 };
 },
);

export const loadProductsPage = createAsyncThunk(
 "admin/loadProductsPage",
 async ({ page = 1, limit = 100, category = "", q = "", maxPrice = "" } = {}, { getState }) => {
  const params = new URLSearchParams({
   page: String(page),
   limit: String(limit),
   sort: "featured",
  });

  if (category) {
   params.set("category", category);
  }

  if (q.trim()) {
   params.set("q", q.trim());
  }

  const cleanMaxPrice = String(maxPrice ?? "").trim();
  if (cleanMaxPrice) {
   params.set("maxPrice", cleanMaxPrice);
  }

  const data = await apiRequest(
   `/products?${params.toString()}`,
   {},
   getToken(getState),
  );

  return {
   products: data.products || [],
   productPagination: data.pagination || { page, limit, total: data.products?.length || 0, totalPages: 1 },
  };
 },
);

export const deleteEnquiry = createAsyncThunk(
 "admin/deleteEnquiry",
 async (enquiryId, { getState }) => {
  await apiRequest(`/enquiries/${enquiryId}`, { method: "DELETE" }, getToken(getState));
  return enquiryId;
 },
);

export const updateCategoryOrder = createAsyncThunk(
 "admin/updateCategoryOrder",
 async (items, { getState }) => {
  const data = await apiRequest(
   "/categories/order",
   {
    method: "PATCH",
    body: JSON.stringify({ items }),
   },
   getToken(getState),
  );
  return data.categories || [];
 },
);

export const updateProductOrder = createAsyncThunk(
 "admin/updateProductOrder",
 async (items, { getState }) => {
  const data = await apiRequest(
   "/products/order",
   {
    method: "PATCH",
    body: JSON.stringify({ items }),
   },
   getToken(getState),
  );
  return data.products || [];
 },
);

const adminSlice = createSlice({
 name: "admin",
 initialState,
 reducers: {
  setStatus(state, action) {
   state.status = action.payload;
  },
  clearStatus(state) {
   state.status.text = "";
  },
  setProducts(state, action) {
   state.products = action.payload;
  },
 },
 extraReducers: (builder) => {
  builder
   .addCase(loadAdminData.pending, (state) => {
    state.loading = true;
   })
   .addCase(loadAdminData.fulfilled, (state, action) => {
    state.loading = false;
    state.categories = action.payload.categories;
    state.products = action.payload.products;
    state.productPagination = action.payload.productPagination || initialState.productPagination;
    state.enquiries = action.payload.enquiries;
    state.sliders = action.payload.sliders;
    state.aboutSliders = action.payload.aboutSliders;
    state.homeJourneyImages = action.payload.homeJourneyImages;
    state.homeCommunityImages = action.payload.homeCommunityImages;
    state.customerFeedback = action.payload.customerFeedback;
    state.scootyFeatureConfig = action.payload.scootyFeatureConfig;
    state.systemCertifications = action.payload.systemCertifications;
    state.productCertifications = action.payload.productCertifications;
    state.status = action.payload.failures.length
     ? { type: "error", text: `Some API routes failed: ${action.payload.failures.join(", ")}` }
     : { type: "success", text: "Dashboard data refreshed." };
   })
   .addCase(loadAdminData.rejected, (state, action) => {
    state.loading = false;
    state.status = { type: "error", text: action.error.message || "Failed to load admin data." };
   })
   .addCase(loadProductsPage.fulfilled, (state, action) => {
    state.products = action.payload.products;
    state.productPagination = action.payload.productPagination;
   })
   .addCase(loadProductsPage.rejected, (state, action) => {
    state.status = { type: "error", text: action.error.message || "Failed to load products." };
   })
   .addCase(deleteEnquiry.fulfilled, (state, action) => {
    state.enquiries = state.enquiries.filter((enquiry) => enquiry.enquiryId !== action.payload);
    state.status = { type: "success", text: "Enquiry deleted." };
   })
   .addCase(deleteEnquiry.rejected, (state, action) => {
    state.status = { type: "error", text: action.error.message || "Failed to delete enquiry." };
   })
   .addCase(updateCategoryOrder.fulfilled, (state, action) => {
    state.categories = action.payload;
    state.status = { type: "success", text: "Category sequence updated." };
   })
   .addCase(updateCategoryOrder.rejected, (state, action) => {
    state.status = { type: "error", text: action.error.message || "Failed to update category sequence." };
   })
   .addCase(updateProductOrder.fulfilled, (state, action) => {
    state.products = action.payload;
    state.status = { type: "success", text: "Product sequence updated." };
   })
   .addCase(updateProductOrder.rejected, (state, action) => {
    state.status = { type: "error", text: action.error.message || "Failed to update product sequence." };
   });
 },
});

export const { clearStatus, setProducts, setStatus } = adminSlice.actions;
export default adminSlice.reducer;


